// Omni-Channel Types for threaded messaging

export type MessageStatusType = 'pending' | 'sent' | 'delayed' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed';

export interface ConversationThread {
  id: string; // Composite key: lead_id or customer_id + tracking_number
  lead_id?: string;
  customer_id?: string;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  lead_number?: string;
  campaign_name?: string;
  tracking_number?: string;

  // Thread metadata
  last_message: string;
  last_message_at: string;
  last_message_direction: 'inbound' | 'outbound';
  last_message_type: 'sms' | 'email';
  unread_count: number;

  // All messages in thread
  messages: ThreadMessage[];
}

export interface ThreadMessage {
  id: string;
  type: 'sms' | 'email';
  direction: 'inbound' | 'outbound';
  content: string;
  timestamp: string;
  sender_name?: string;
  // SMS-specific
  from_number?: string;
  to_number?: string;
  // Email-specific
  from_email?: string;
  to_email?: string;
  subject?: string;
  // Engagement tracking (email)
  opened_count?: number;
  clicked_count?: number;
  bounce_reason?: string;
  // Common
  status?: MessageStatusType;
  media_urls?: string[];
}

// Local storage key prefix for unread tracking
export const THREAD_VIEWED_PREFIX = 'thread_viewed_';

// Helper to get thread viewed timestamp from localStorage
export function getThreadViewedAt(threadId: string): string | null {
  return localStorage.getItem(`${THREAD_VIEWED_PREFIX}${threadId}`);
}

// Helper to set thread as viewed
export function markThreadAsViewed(threadId: string): void {
  localStorage.setItem(`${THREAD_VIEWED_PREFIX}${threadId}`, new Date().toISOString());
}

// Helper to calculate unread count for a thread
export function calculateUnreadCount(thread: ConversationThread): number {
  const lastViewed = getThreadViewedAt(thread.id);
  if (!lastViewed) {
    // If never viewed, count all inbound messages as unread
    return thread.messages.filter(m => m.direction === 'inbound').length;
  }
  // Count inbound messages after last viewed timestamp
  return thread.messages.filter(
    m => m.direction === 'inbound' && new Date(m.timestamp) > new Date(lastViewed)
  ).length;
}
